/**
 * Rule-generated visits the plan of care does not carry:
 *  - RN supervisory every 14 days while an aide is active — co-scheduled onto a
 *    skilled nursing visit already inside the window, standalone only when none is;
 *  - recertification assessment on days 56–60 of the cert period.
 */
import type { ConfigState, DateKey, Discipline, Episode, PlanVisit } from "../state";
import { addDaysToKey, daysBetweenKeys, isWorkingDayKey } from "./clock";
import { weekIndexFor } from "./planExpansion";

export const SUPERVISORY_INTERVAL_DAYS = 14;
export const RECERT_WINDOW_START_DAY = 56; // 1-based day of the cert period
export const RECERT_WINDOW_END_DAY = 60;

const SKILLED_NURSING_KINDS = new Set(["SOC", "ROC", "RECERT", "SN_ROUTINE"]);

function isActive(v: PlanVisit): boolean {
  return v.status !== "canceled" && v.status !== "missed";
}

export function applySupervisoryRule(visits: PlanVisit[], episode: Episode, config: ConfigState): PlanVisit[] {
  const aide = visits.filter((v) => v.discipline === "HHA" && isActive(v)).map((v) => v.targetDate).sort();
  if (aide.length === 0) return visits;
  const out = visits.map((v) => ({ ...v }));
  const firstAide = aide[0];
  const lastAide = aide[aide.length - 1];
  let counter = 0;
  for (let blockStart = firstAide; blockStart <= lastAide; blockStart = addDaysToKey(blockStart, SUPERVISORY_INTERVAL_DAYS)) {
    const blockEnd = addDaysToKey(blockStart, SUPERVISORY_INTERVAL_DAYS - 1);
    const candidate = out
      .filter((v) => v.discipline === "RN" && SKILLED_NURSING_KINDS.has(v.visitTypeKind) && isActive(v) && v.targetDate >= blockStart && v.targetDate <= blockEnd && !v.rule)
      .sort((a, b) => a.targetDate.localeCompare(b.targetDate))[0];
    if (candidate) {
      candidate.rule = "supervisory";
    } else if (blockEnd <= episode.certEndDate) {
      out.push({
        id: `${episode.id}:rule:supervisory:${++counter}`,
        episodeId: episode.id,
        discipline: "RN",
        visitTypeKind: "RN_SUPERVISORY",
        weekIndex: weekIndexFor(episode, blockEnd),
        targetDate: blockEnd,
        requiredTraits: config.visitTypes.RN_SUPERVISORY.requiredTraits,
        status: "placeholder",
        rule: "supervisory",
        planVersion: 0,
      });
    }
  }
  return out.sort((a, b) => a.targetDate.localeCompare(b.targetDate) || a.discipline.localeCompare(b.discipline));
}

/**
 * Day 56 — unless that is a weekend, when the assessment steps to the first
 * working day inside the window. Days 56–60 is five consecutive days, so one of
 * them is always a weekday. The branch's weekend belongs to per diems and
 * on-call, so an assessment that plots on a Saturday reads as a visit nobody is
 * there to make; worse, `selectWeekPoints` counts it and My week draws no card
 * for it, so the day carries points with nothing underneath them.
 */
export function recertTargetDate(episode: Pick<Episode, "socDate">): DateKey {
  const { start, end } = recertWindow(episode);
  for (let day = start; day <= end; day = addDaysToKey(day, 1)) if (isWorkingDayKey(day)) return day;
  return start;
}

export function recertWindow(episode: Pick<Episode, "socDate">): { start: DateKey; end: DateKey } {
  return { start: addDaysToKey(episode.socDate, RECERT_WINDOW_START_DAY - 1), end: addDaysToKey(episode.socDate, RECERT_WINDOW_END_DAY - 1) };
}

export function applyRecertRule(visits: PlanVisit[], episode: Episode, config: ConfigState, socDiscipline: Discipline): PlanVisit[] {
  if (visits.some((v) => v.rule === "recert")) return visits;
  const targetDate = recertTargetDate(episode);
  const recert: PlanVisit = {
    id: `${episode.id}:rule:recert`,
    episodeId: episode.id,
    discipline: socDiscipline,
    visitTypeKind: "RECERT",
    weekIndex: weekIndexFor(episode, targetDate),
    targetDate,
    requiredTraits: config.visitTypes.RECERT.requiredTraits,
    status: "placeholder",
    rule: "recert",
    planVersion: 0,
  };
  return [...visits, recert].sort((a, b) => a.targetDate.localeCompare(b.targetDate) || a.discipline.localeCompare(b.discipline));
}

export function dayOfCert(episode: Pick<Episode, "socDate">, date: DateKey): number {
  return daysBetweenKeys(episode.socDate, date) + 1;
}
